import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useHotel } from "@/lib/hotel";
import { TID } from "@/constants/testIds/aura";
import { ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const { ctx } = useHotel();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", date_of_birth: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, role: "patient" });
      toast.success("Welcome to AllRound Concierge");
      // If a QR scan opened a RoomServiceSession, confirm the stay next.
      nav(ctx?.session_id ? "/stay/confirm" : "/emergency");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const startEmergentSso = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    sessionStorage.setItem("aura_sso_role_hint", "patient");
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-warm">
      <section className="bg-forest text-cream relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-6xl mx-auto px-6 pt-6 pb-14 relative">
          <Link to="/" className="inline-flex items-center gap-1 text-cream/70 text-sm hover:text-cream">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="max-w-lg mt-10">
            <div className="eyebrow eyebrow-white">Create account</div>
            <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] mt-3">
              A private door,<br />
              <span className="italic">to a real doctor.</span>
            </h1>
            <p className="text-cream/70 mt-4 leading-relaxed">
              Minimal info. Fully confidential. Nothing you enter here is shared with hotel staff.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-md mx-auto px-6 -mt-8 pb-16">
        <div className="card-lux p-7 fade-up">
          <form onSubmit={submit} className="space-y-4">
            <Field label="Full name">
              <input required value={form.full_name} onChange={set("full_name")}
                className="h-12 w-full rounded-full border border-[rgba(33,59,42,0.10)] px-5 bg-white"
                data-testid={TID.nameInput} placeholder="Your name" />
            </Field>
            <Field label="Email">
              <input type="email" required value={form.email} onChange={set("email")}
                className="h-12 w-full rounded-full border border-[rgba(33,59,42,0.10)] px-5 bg-white"
                data-testid={TID.emailInput} placeholder="you@example.com" />
            </Field>
            <Field label="Password">
              <input type="password" required minLength={6} value={form.password} onChange={set("password")}
                className="h-12 w-full rounded-full border border-[rgba(33,59,42,0.10)] px-5 bg-white"
                data-testid={TID.passwordInput} placeholder="At least 6 characters" />
            </Field>
            <Field label="Date of birth (optional)">
              <input type="date" value={form.date_of_birth} onChange={set("date_of_birth")}
                className="h-12 w-full rounded-full border border-[rgba(33,59,42,0.10)] px-5 bg-white" />
            </Field>
            <button type="submit" className="btn-primary w-full" disabled={loading} data-testid={TID.submitBtn}>
              {loading ? "Creating…" : (<>Continue <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-[rgba(33,59,42,0.10)]" />
            <div className="text-[11px] uppercase tracking-widest text-stone-400">or</div>
            <div className="flex-1 h-px bg-[rgba(33,59,42,0.10)]" />
          </div>

          <button
            type="button"
            onClick={startEmergentSso}
            className="btn-secondary w-full"
            data-testid="sso-emergent-register-btn"
          >
            Continue with Google
          </button>

          <p className="text-sm text-stone-500 text-center pt-5">
            Already registered?{" "}
            <Link to="/login" className="link-under font-medium" data-testid={TID.toLogin}>
              Sign in
            </Link>
          </p>
        </div>

        <div className="mt-5 flex items-start gap-3 px-2">
          <ShieldCheck className="h-4 w-4 text-sage-500 mt-0.5 shrink-0" />
          <p className="text-xs text-stone-500 leading-relaxed">
            Your account is separate from your clinical record. Hotel staff can never see your
            symptoms or diagnosis.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-stone-500 mb-2 font-semibold">{label}</div>
      {children}
    </label>
  );
}
